import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { REACTION_EMOJIS } from '../constants/reactions';
import './MatchReactionsModal.css';

/**
 * Modal showing all reactions for a match, grouped by team and emoji (LinkedIn/Facebook style).
 * @param {string} matchId - Match document id
 * @param {string} player1Name - Display name for team 1
 * @param {string} player2Name - Display name for team 2
 * @param {function} onClose - Close callback
 */
export default function MatchReactionsModal({ matchId, player1Name = 'Player 1', player2Name = 'Player 2', onClose }) {
  const [loading, setLoading] = useState(true);
  const [byTeamAndEmoji, setByTeamAndEmoji] = useState({ player1: {}, player2: {} });

  useEffect(() => {
    if (!matchId) {
      setLoading(false);
      return;
    }
    const reactionsRef = collection(db, 'matches', matchId, 'reactions');
    getDocs(reactionsRef)
      .then((snap) => {
        const player1 = {};
        const player2 = {};
        REACTION_EMOJIS.forEach((emoji) => {
          player1[emoji] = [];
          player2[emoji] = [];
        });
        snap.docs.forEach((d) => {
          const data = d.data();
          const emoji = data.emoji;
          const team = data.team;
          const name = data.displayName || 'Someone';
          if (REACTION_EMOJIS.includes(emoji) && (team === 'player1' || team === 'player2')) {
            const target = team === 'player1' ? player1 : player2;
            if (!target[emoji]) target[emoji] = [];
            target[emoji].push(name);
          }
        });
        setByTeamAndEmoji({ player1, player2 });
      })
      .catch(() => setByTeamAndEmoji({ player1: {}, player2: {} }))
      .finally(() => setLoading(false));
  }, [matchId]);

  const totalCount = Object.values(byTeamAndEmoji.player1).flat().length + Object.values(byTeamAndEmoji.player2).flat().length;

  return (
    <div className="modal-overlay reactions-modal-overlay" onClick={onClose} role="dialog" aria-label="Reactions by team and emoji">
      <div className="confirmation-modal match-reactions-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Reactions</h3>
        {loading ? (
          <p className="reactions-modal-loading">Loading…</p>
        ) : totalCount === 0 ? (
          <p className="reactions-modal-empty">No reactions yet.</p>
        ) : (
          <div className="reactions-modal-by-team">
            <section className="reactions-modal-team">
              <h4 className="reactions-modal-team-name">{player1Name}</h4>
              <div className="reactions-modal-emoji-list">
                {REACTION_EMOJIS.map((emoji) => {
                  const names = byTeamAndEmoji.player1[emoji] || [];
                  if (names.length === 0) return null;
                  return (
                    <div key={emoji} className="reactions-modal-emoji-row">
                      <span className="reactions-modal-emoji" aria-hidden>{emoji}</span>
                      <span className="reactions-modal-names">{names.join(', ')}</span>
                    </div>
                  );
                })}
              </div>
            </section>
            <section className="reactions-modal-team">
              <h4 className="reactions-modal-team-name">{player2Name}</h4>
              <div className="reactions-modal-emoji-list">
                {REACTION_EMOJIS.map((emoji) => {
                  const names = byTeamAndEmoji.player2[emoji] || [];
                  if (names.length === 0) return null;
                  return (
                    <div key={emoji} className="reactions-modal-emoji-row">
                      <span className="reactions-modal-emoji" aria-hidden>{emoji}</span>
                      <span className="reactions-modal-names">{names.join(', ')}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        )}
        <button type="button" onClick={onClose} className="btn-secondary">
          Close
        </button>
      </div>
    </div>
  );
}
